import { Button, HSpacer, InfoTooltip, Text, TextLink, VSpacer } from '@/components/DesignSystem';
import { FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { ChurnStatusChip } from '@/components/shared/ChurnStatusChip';
import { TruncatedHeaderText } from '@/components/shared/TruncatedHeaderText';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { CustomerChurn } from '@/constants/constant';
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { LinkedAccountsDialog } from '@/pages/FarmerList/LinkedAccountsDialog';
import { LocationsDialog } from '@/pages/RetailerOverview/PointsOverview/LocationsDialog';
import {
  PointsOverviewToolbar,
} from '@/pages/RetailerOverview/PointsOverview/PointsOverviewToolbar';
import { SalespeopleDialog } from '@/pages/RetailerOverview/PointsOverview/SalespeopleDialog';
import {
  ViewCustomerDetailsFullScreenModal,
} from '@/pages/RetailerOverview/ViewCustomerDetailsFullScreenModal';
import {
  ProgramPeriodFilterChip,
} from '@/pages/RetailerOverview/ProgramPeriodFilterChip';
import { ExportReportDialog } from '@/pages/Reports/ExportReportDialog';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { RetailerEndpoint } from '@api/endpoints';
import { ApiRetailer, ApiRetailerLocation, ApiUser } from '@api/interfaces';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material';
import { ChurnStatus, ReportType } from '@shared/enums';
import {
  formatDateOnly,
  formatNumber,
  formatPercent,
  getAccountName,
  getTierName,
  omit,
} from '@shared/utilities';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from 'react-query';
import { agronomyProgramsOnly } from '@/utilities/RewardsProgramUtility';

interface CustomerPointsReportProps {
  onCloseExportDialog: () => void,
  onProgramChange?: (program: ApiRewardsProgram) => void,
  retailer: ApiRetailer,
  selectedProgram?: ApiRewardsProgram,
  showExportConfirmationDialog: boolean,
}

export const CustomerPointsReport = ({
  onCloseExportDialog,
  onProgramChange,
  retailer,
  selectedProgram,
  showExportConfirmationDialog,
}: CustomerPointsReportProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [sortBy, setSortBy] = (
    useState<RetailerEndpoint.GetPointsKPIsByCustomer.CustomerSort>('availablePoints')
  );
  const [sortDesc, setSortDesc] = useState(true);
  const [filterBody, setFilterBody] = useState<
    RetailerEndpoint.GetPointsKPIsByCustomer.Request & { period?: string, periodCount?: number }
  >({});
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => new Map());
  const [hasPurchasedCategory, setHasPurchasedCategory] = useState<boolean | undefined>(
    undefined,
  );
  const [purchaseCategoryIds, setPurchaseCategoryIds] = useState<string[]>([]);
  const [selectedLinkedAccountsCustomer, setSelectedLinkedAccountsCustomer] = useState<ApiUser>();
  const [selectedViewAccountCustomer, setSelectedViewAccountCustomer] = useState<ApiUser>();
  const [customerSalespeopleToDisplay, setCustomerSalespeopleToDisplay] = (
    useState<{ customer: ApiUser, salespeople: ApiUser[] }>()
  );
  const [customerLocationsToDisplay, setCustomerLocationsToDisplay] = (
    useState<{ customer: ApiUser, locations: ApiRetailerLocation[] }>()
  );
  const filterIds = [
    'wallet',
    'customer',
    'salesperson',
    'location',
    'purchase-category',
  ];

  const hasPurchaseCategorySelections = Array.from(filterSelections.values())
    .some((s) => s.size > 0);
  const hasActiveFilters = hasPurchaseCategorySelections || purchaseCategoryIds.length > 0;

  const handleClearAll = () => {
    setFilterBody({});
    setFilterSelections(new Map());
    setHasPurchasedCategory(undefined);
    setPurchaseCategoryIds([]);
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (!tableRef.current) return;
      const { top } = tableRef.current.getBoundingClientRect();
      // Subtract the top offset and the fixed footer height (64px)
      setTableHeight(window.innerHeight - top - 64);
    };

    measure();

    const observer = new ResizeObserver(measure);
    if (tableRef.current?.parentElement) {
      observer.observe(tableRef.current.parentElement);
    }
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const { isRetailerAdmin } = useAuthentication();
  const showChurnData = isRetailerAdmin && retailer.isChurnEnabled;

  const queryBody: RetailerEndpoint.GetPointsKPIsByCustomer.Request = {
    ...omit(filterBody, ['period', 'periodCount']),
    ...(selectedProgram ? { programId: selectedProgram.id } : {}),
  };

  const { churnStatus } = CustomerChurn.columns;

  const {
    data: customerKPIs,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [
      QueryKeys.GET_POINTS_KPIS_BY_CUSTOMER,
      selectedProgram,
      retailer.id,
      filterBody,
      sortBy,
      sortDesc,
    ],
    async ({ pageParam }) => (
      RetailerApi.getPointsKPIsByCustomer(
        retailer.id,
        {
          limit: 20,
          page: pageParam,
          sort: sortBy,
          sortDesc,
          ...queryBody,
        },
      )
    ),
    {
      getNextPageParam: (lastPage) => (
        lastPage.lastPage > lastPage.page
          ? lastPage.page + 1
          : undefined
      ),
    },
  );

  const handleSort = (newSortBy: RetailerEndpoint.GetPointsKPIsByCustomer.CustomerSort) => {
    const isSortCurrentlySelected = newSortBy === sortBy;
    if (isSortCurrentlySelected) {
      if (newSortBy === 'availablePoints') {
        setSortDesc(!sortDesc);
      } else {
        if (!sortDesc) {
          setSortBy('availablePoints');
          setSortDesc(true);
        } else {
          setSortDesc(false);
        }
      }
    } else {
      setSortBy(newSortBy);
      setSortDesc(true);
    }
  };

  // Retailer-level flag carried on every row, so the column does not come and go with the page.
  const hasLockedProgramYear = (
    customerKPIs?.pages?.[0]?.data?.[0]?.hasLockedProgramYear ?? false
  );

  const renderTableData = () => {
    if (isLoading) {
      return null;
    }
    return (
      (customerKPIs?.pages?.flatMap(({ data }) => data) ?? [])
        .map(({
          availablePoints,
          customer,
          rankCurrent,
          linkedAccounts,
          locations,
          lockedPoints,
          pointsRedeemed,
          pointsRedemptionRate,
          reservedPoints,
          salespersons,
        }) => (
          <TableRow key={customer.id}>
            <TableCell height={67} sx={{ overflowWrap: 'anywhere' }} width={255}>
              {getAccountName(customer)}
              <Text category="body-xsmall" sx={{ color: theme.palette.text.secondary }}>
                ID: #{customer.externalId}
              </Text>
            </TableCell>
            {!!selectedProgram && (
              <TableCell height={67} width={180}>
                {getTierName(
                  selectedProgram.tiers ?? [],
                  rankCurrent,
                  t($ => $.common.status.none),
                )}
              </TableCell>
            )}
            <TableCell align="right" height={67} sx={{ paddingRight: '40px' }} width={140}>
              {formatNumber(availablePoints, i18n)}
            </TableCell>
            {retailer.hasBookings && (
              <TableCell align="right" height={67} sx={{ paddingRight: '40px' }} width={140}>
                {formatNumber(reservedPoints, i18n)}
              </TableCell>
            )}
            {hasLockedProgramYear && (
              <TableCell align="right" height={67} sx={{ paddingRight: '40px' }} width={140}>
                {formatNumber(lockedPoints, i18n)}
              </TableCell>
            )}
            <TableCell align="right" height={67} sx={{ paddingRight: '40px' }} width={140}>
              {formatNumber(pointsRedeemed, i18n)}
            </TableCell>
            <TableCell align="right" height={67} sx={{ paddingRight: '40px' }} width={140}>
              {formatPercent(pointsRedemptionRate * 100, i18n, { minimumFractionDigits: 2 })}
            </TableCell>
            {showChurnData && (
              <TableCell height={67} width={180}>
                <ChurnStatusChip status={customer?.churnStatus as ChurnStatus} />
              </TableCell>
            )}
            <TableCell height={67} width={130}>
              {!salespersons?.length
                ? t($ => $.common.none)
                : (
                  <TextLink
                    onClick={(e) => {
                      e.preventDefault();
                      setCustomerSalespeopleToDisplay({ customer, salespeople: salespersons });
                    }}
                    testID={`${customer.id}-salespeople-button`}
                  >
                    {t($ => $.common.units.salespeopleCount, { count: salespersons.length })}
                  </TextLink>
                )
              }
            </TableCell>
            <TableCell height={67} width={115}>
              {!locations?.length
                ? t($ => $.common.none)
                : (
                  <TextLink
                    onClick={(e) => {
                      e.preventDefault();
                      setCustomerLocationsToDisplay({ customer, locations });
                    }}
                    testID={`${customer.id}-locations-button`}
                  >
                    {t($ => $.common.units.locationsCount, { count: locations.length })}
                  </TextLink>
                )
              }
            </TableCell>
            <TableCell height={67} width={115}>
              {!linkedAccounts?.length
                ? t($ => $.customers.linkedAccounts.none)
                : (
                  <TextLink
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedLinkedAccountsCustomer(customer);
                    }}
                    testID={`${customer.id}-linked-accounts-button`}
                  >
                    {t($ => $.common.units.accountsCount, { count: linkedAccounts.length })}
                  </TextLink>
                )
              }
            </TableCell>
            <TableCell height={67} width={132}>
              <Button
                onClick={() => setSelectedViewAccountCustomer(customer)}
                size="small"
                square
                testID={`${customer.id}-view-account-button`}
              >
                {t($ => $.common.actions.viewAccount)}
              </Button>
            </TableCell>
          </TableRow>
        ))
    );
  };

  const isSelectedProgramExpired = selectedProgram ? selectedProgram.endDate < formatDateOnly(
    new Date(), 'America/Los_Angeles',
  ) : false;

  const totalCount = customerKPIs?.pages?.[0]?.total ?? 0;
  return (
    <Stack>
      <Stack alignItems="center" direction="row" flexWrap="wrap">
        <PointsOverviewToolbar
          filterIds={filterIds}
          filterSelections={filterSelections}
          hasPurchasedCategory={hasPurchasedCategory}
          onChange={(body, selections) => {
            setFilterBody(body);
            setFilterSelections(selections ?? new Map());
          }}
          onChangePurchaseCategory={(ids, hasPurchased) => {
            setHasPurchasedCategory(hasPurchased);
            setPurchaseCategoryIds(ids);
          }}
          purchaseCategoryIds={purchaseCategoryIds}
          retailer={retailer}
          selectedProgramId={selectedProgram?.id}
        />
        {!!onProgramChange && (
          <>
            <HSpacer size="3" />
            <ProgramPeriodFilterChip
              onSelectProgram={onProgramChange}
              programs={agronomyProgramsOnly(retailer.rewardsPrograms)}
              selectedProgram={selectedProgram}
              testID="customer-points-program-period-filter-chip"
            />
          </>
        )}
        {hasActiveFilters && (
          <>
            <HSpacer size="3" />
            <TextLink
              category="title-medium"
              onClick={handleClearAll}
              testID="customer-points-filters-clear-all-button"
            >
              {t(($) => $.common.actions.clearAll)}
            </TextLink>
          </>
        )}
      </Stack>
      <VSpacer size="3" />
      <Stack direction="row" justifyContent="space-between">
        <Text category="body-small" textAlign="left">
          {t($ => $.common.units.itemsCount, { count: totalCount })}
        </Text>
        {!retailer.isProgramCreationDisabled &&
          <Text category="body-small" textAlign="right">
            {t($ => $.overview.selectedProgramPeriodOnly)}
          </Text>
        }
      </Stack>
      <VSpacer size="3" />
      <div ref={tableRef} />
      <InfiniteScroll
        fetchMoreItems={fetchNextPage}
        hasMore={hasNextPage ?? true}
        height={tableHeight > 0 ? `${tableHeight}px` : undefined}
        isLoading={isLoading || isFetchingNextPage}
      >
        <Table sx={{ '& .MuiTableCell-body': { py: '4px' }, tableLayout: 'fixed' }}>
          <TableHead
            sx={{
              '& .MuiTableCell-head': { py: '4px' },
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell height={76} variant="head" width={255}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  <TruncatedHeaderText>{t($ => $.common.customer)}</TruncatedHeaderText>
                </TableSortLabel>
              </TableCell>
              {!!selectedProgram && (
                <TableCell height={76} variant="head" width={180}>
                  <TableSortLabel
                    active={sortBy === 'rankCurrent'}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort('rankCurrent')}
                  >
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.currentStatus)}
                      {retailer.isProgramCreationDisabled ? '' : '*'}
                    </TruncatedHeaderText>
                    <HSpacer size="2" />
                    <InfoTooltip color="info">
                      {t($ => $.overview.kpis.currentStatusTooltip)}
                    </InfoTooltip>
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell align="right" height={76} variant="head" width={140}>
                <TableSortLabel
                  active={sortBy === 'availablePoints'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('availablePoints')}
                  sx={{ flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.availablePoints)}
                      {!retailer.isProgramCreationDisabled && isSelectedProgramExpired ? '*' : ''}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              {retailer.hasBookings && (
                <TableCell align="right" height={76} variant="head" width={140}>
                  <TableSortLabel
                    active={sortBy === 'reservedPoints'}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort('reservedPoints')}
                    sx={{ flexDirection: 'row' }}
                  >
                    <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                      <TruncatedHeaderText>
                        {t($ => $.overview.kpis.reservedPoints)}
                        {!retailer.isProgramCreationDisabled && isSelectedProgramExpired ? '*' : ''}
                      </TruncatedHeaderText>
                    </Box>
                  </TableSortLabel>
                </TableCell>
              )}
              {hasLockedProgramYear && (
                <TableCell align="right" height={76} variant="head" width={140}>
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.wallet.locked.title)}
                      {!retailer.isProgramCreationDisabled && isSelectedProgramExpired ? '*' : ''}
                    </TruncatedHeaderText>
                  </Box>
                </TableCell>
              )}
              <TableCell align="right" height={76} variant="head" width={140}>
                <TableSortLabel
                  active={sortBy === 'pointsRedeemed'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('pointsRedeemed')}
                  sx={{ flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.redeemedPoints)}
                      {retailer.isProgramCreationDisabled ? '' : '*'}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" height={76} variant="head" width={140}>
                <TableSortLabel
                  active={sortBy === 'pointsRedemptionRate'}
                  direction={sortDesc ? 'desc' : 'asc'}
                  onClick={() => handleSort('pointsRedemptionRate')}
                  sx={{ flexDirection: 'row' }}
                >
                  <Box component="span" sx={{ textAlign: 'left', width: 'min-content' }}>
                    <TruncatedHeaderText>
                      {t($ => $.overview.kpis.redemptionRate)}
                      {retailer.isProgramCreationDisabled ? '' : '*'}
                    </TruncatedHeaderText>
                  </Box>
                </TableSortLabel>
              </TableCell>
              {showChurnData && (
                <TableCell height={76} variant="head" width={180}>
                  <TableSortLabel
                    active={sortBy === churnStatus?.key}
                    direction={sortDesc ? 'desc' : 'asc'}
                    onClick={() => handleSort(
                      churnStatus?.key as RetailerEndpoint.GetPointsKPIsByCustomer.CustomerSort,
                    )}
                  >
                    <TruncatedHeaderText>
                      {churnStatus?.label} (Beta)
                    </TruncatedHeaderText>
                    <HSpacer size="2" />
                    <InfoTooltip color="info">
                      {churnStatus?.description}
                    </InfoTooltip>
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell height={76} variant="head" width={130}>
                <TruncatedHeaderText>{t($ => $.overview.kpis.salesperson)}</TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={115}>
                <TruncatedHeaderText>{t($ => $.overview.kpis.location)}</TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={115}>
                <TruncatedHeaderText>
                  {t($ => $.customers.linkedAccounts.title)}
                </TruncatedHeaderText>
              </TableCell>
              <TableCell height={76} variant="head" width={132} />
            </TableRow>
          </TableHead>
          <TableBody>
            {renderTableData()}
          </TableBody>
        </Table>
      </InfiniteScroll>
      {showExportConfirmationDialog && (
        <ExportReportDialog
          confirmMessage={t($ => $.overview.exportTableConfirmation)}
          onClose={onCloseExportDialog}
          request={{
            params: { sort: sortBy, sortDesc, ...queryBody },
            type: ReportType.CustomerPoints,
          }}
          retailerId={retailer.id}
        />
      )}
      {!!selectedLinkedAccountsCustomer && (
        <LinkedAccountsDialog
          customer={selectedLinkedAccountsCustomer}
          onClose={() => setSelectedLinkedAccountsCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
          show
        />
      )}
      {!!customerLocationsToDisplay && (
        <LocationsDialog
          customer={customerLocationsToDisplay.customer}
          locations={customerLocationsToDisplay.locations}
          onClose={() => setCustomerLocationsToDisplay(undefined)}
        />
      )}
      {!!customerSalespeopleToDisplay && (
        <SalespeopleDialog
          customer={customerSalespeopleToDisplay.customer}
          onClose={() => setCustomerSalespeopleToDisplay(undefined)}
          salespeople={customerSalespeopleToDisplay.salespeople}
        />
      )}
      {!!selectedViewAccountCustomer && (
        <ViewCustomerDetailsFullScreenModal
          id={selectedViewAccountCustomer.id}
          onClose={() => setSelectedViewAccountCustomer(undefined)}
          onViewAccount={(customer) => {
            setSelectedViewAccountCustomer(customer);
          }}
        />
      )}
    </Stack>
  );
};
